import { http, HttpResponse, PathParams } from "msw";

import { RepoScope } from "@protos/permissions/v2/scopes";
import {
  DeploymentRepoListResponse,
  DeploymentRepoPaginatedListResponse,
  Repo,
  RepoChanges,
  SubmitDeploymentRepoResponse,
} from "@shared/types";

const ALL_REPO_SCOPES = [
  RepoScope.ASSIGN_TEAM,
  RepoScope.DELETE_PROJECT,
  RepoScope.MANAGE_PROJECT_SETTINGS,
  RepoScope.READ_FINDING,
  RepoScope.TRIAGE_FINDING,
];
const now = new Date();
export const TEST_REPOS: Repo[] = [
  {
    id: 1,
    deployment_id: 1,
    name: "test-repo",
    policies: [],
    ignored_files: null,
    product_ignored_files: {
      sast: [],
      sca: [],
      secrets: [],
    },
    provisioned_at: "2021-01-01T00:00:00Z",
    first_scan_id: 0,
    latest_scan_id: 1,
    latest_scan: {},
    latest_scan_at: "2021-01-02T01:01:01Z",
    latest_full_sca_scan_at: "2021-01-01T00:00:00Z",
    latest_full_sca_scan_dependency_parser_errors: null,
    autofix: false,
    is_disconnected: false,
    sca_settings: {},
    sca_info: { dependency_counts: { "yarn.lock": 1243 } },
    rule_exclusions: [],
    is_setup: true,
    authorized_scopes: ALL_REPO_SCOPES,
    managed_scans: null,
    primary_ref: {
      id: 2,
      ref: "refs/heads/release_1",
      is_override: true,
    },
    default_ref: {
      id: 1,
      ref: "refs/heads/main",
    },
    refs: [
      {
        repo_ref_id: 1,
        ref: "refs/heads/main",
        is_default: true,
        is_primary: false,
      },
      {
        repo_ref_id: 2,
        ref: "refs/heads/release_1",
        is_default: false,
        is_primary: true,
      },
    ],
  },
  {
    id: 2,
    deployment_id: 1,
    name: "starting-repo",
    policies: [],
    ignored_files: null,
    product_ignored_files: {
      sast: [],
      sca: [],
      secrets: [],
    },
    provisioned_at: now.toISOString(),
    latest_scan_at: null,
    first_scan_id: null,
    latest_scan_id: null,
    latest_scan: null,
    latest_full_sca_scan_at: null,
    latest_full_sca_scan_dependency_parser_errors: null,
    autofix: false,
    is_disconnected: false,
    sca_settings: {},
    sca_info: { dependency_counts: { "yarn.lock": 1243 } },
    rule_exclusions: [],
    is_setup: true,
    authorized_scopes: ALL_REPO_SCOPES,
    managed_scans: null,
  },
  {
    id: 3,
    deployment_id: 1,
    name: "never-started-repo",
    policies: [],
    ignored_files: null,
    product_ignored_files: {
      sast: [],
      sca: [],
      secrets: [],
    },
    provisioned_at: "2021-01-01T00:00:00Z",
    latest_scan_at: null,
    first_scan_id: null,
    latest_scan_id: null,
    latest_scan: null,
    latest_full_sca_scan_at: null,
    latest_full_sca_scan_dependency_parser_errors: null,
    autofix: false,
    is_disconnected: false,
    sca_settings: {},
    sca_info: { dependency_counts: { "yarn.lock": 1243 } },
    rule_exclusions: [],
    is_setup: true,
    authorized_scopes: ALL_REPO_SCOPES,
    managed_scans: null,
  },
  {
    id: 4,
    deployment_id: 1,
    name: "just-scanned-repo",
    policies: [],
    ignored_files: null,
    product_ignored_files: {
      sast: [],
      sca: [],
      secrets: [],
    },
    provisioned_at: "2022-01-01T00:00:00Z",
    latest_scan_at: now.toISOString(),
    latest_full_sca_scan_at: now.toISOString(),
    latest_full_sca_scan_dependency_parser_errors: null,
    latest_scan_id: 3,
    latest_scan: {},
    first_scan_id: 2,
    autofix: false,
    is_disconnected: false,
    sca_settings: {},
    sca_info: { dependency_counts: { "yarn.lock": 1243 } },
    rule_exclusions: [],
    is_setup: true,
    authorized_scopes: ALL_REPO_SCOPES,
    managed_scans: null,
  },
  {
    id: 5,
    deployment_id: 1,
    name: "repository",
    policies: [],
    ignored_files: null,
    product_ignored_files: {
      sast: [],
      sca: [],
      secrets: [],
    },
    provisioned_at: "2021-01-01T00:00:00Z",
    first_scan_id: 4,
    latest_scan_id: 4,
    latest_scan: {},

    latest_scan_at: "2021-01-01T00:00:00Z",
    latest_full_sca_scan_at: "2021-01-01T00:00:00Z",
    latest_full_sca_scan_dependency_parser_errors: null,
    autofix: false,
    is_disconnected: false,
    sca_settings: {},
    sca_info: { dependency_counts: { "yarn.lock": 1243 } },
    rule_exclusions: [],
    is_setup: true,
    authorized_scopes: ALL_REPO_SCOPES,
    managed_scans: null,
  },
];

export const repoHandlersStatic = [
  http.get<PathParams, DeploymentRepoListResponse>(
    "*/api/agent/deployments/:deploymentId/repos",
    () => HttpResponse.json({ repos: TEST_REPOS }, { status: 200 })
  ),
  http.get<PathParams, DeploymentRepoPaginatedListResponse>(
    "*/api/agent/deployments/:deploymentId/repos/v2",
    () => HttpResponse.json({ repos: TEST_REPOS }, { status: 200 })
  ),
  http.get<PathParams, DeploymentRepoPaginatedListResponse>(
    "*/api/agent/deployments/:deploymentId/repos/count",
    () => HttpResponse.json({ count: TEST_REPOS.length }, { status: 200 })
  ),
  // for fetching repos by name - the name may have a / in it
  http.get("*/api/agent/deployments/:deploymentId/repos/:repoName/*", () =>
    HttpResponse.json({ repo: TEST_REPOS[0] }, { status: 200 })
  ),
  http.put<PathParams, RepoChanges, SubmitDeploymentRepoResponse>(
    "*/api/agent/deployments/:deploymentId/repos/:repoName*",
    () => HttpResponse.json(TEST_REPOS[0], { status: 200 })
  ),
  http.patch("*/api/agent/deployments/:deploymentId/repos/:repoId", () =>
    HttpResponse.json(TEST_REPOS[0], { status: 200 })
  ),
  http.delete("*/api/agent/deployments/:deploymentId/repos/:repoId", () =>
    HttpResponse.json("OK", { status: 200 })
  ),
];
